import React, { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PaperPlaneTilt, CircleNotch, CheckCircle } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const budgetRanges = [
  "$1,000 - $2,500",
  "$2,500 - $5,000",
  "$5,000 - $10,000",
  "$10,000 - $25,000",
  "$25,000+",
];

const collabTypes = [
  "Instagram Reels",
  "Instagram Stories",
  "YouTube Integration",
  "Full Campaign",
  "Brand Ambassador",
  "Event Appearance",
];

export default function ContactForm() {
  const [formData, setFormData] = useState({
    brand_name: "",
    contact_name: "",
    email: "",
    phone: "",
    budget_range: "",
    collaboration_type: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    try {
      await axios.post(`${API}/inquiries`, formData);
      setStatus("success");
      setFormData({
        brand_name: "",
        contact_name: "",
        email: "",
        phone: "",
        budget_range: "",
        collaboration_type: "",
        message: "",
      });
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err.response?.data?.detail || "Something went wrong. Please try again."
      );
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A] relative"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-4">
            Let's Work Together
          </p>
          <h2
            data-testid="contact-heading"
            className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white mb-4"
          >
            Start Your Collaboration
          </h2>
          <p className="text-base text-white/40 max-w-lg mx-auto">
            Fill out the form below and I'll get back to you within 24 hours
            with a custom proposal.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="bg-[#121212] border border-white/10 rounded-xl p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-white/50 mb-2">
                Brand Name *
              </label>
              <Input
                name="brand_name"
                value={formData.brand_name}
                onChange={handleChange}
                required
                data-testid="input-brand-name"
                placeholder="Your brand name"
                className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#FF3B30] focus:border-transparent h-12"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-white/50 mb-2">
                Contact Name *
              </label>
              <Input
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                required
                data-testid="input-contact-name"
                placeholder="Your full name"
                className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#FF3B30] focus:border-transparent h-12"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-white/50 mb-2">
                Email *
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="input-email"
                placeholder="your@email.com"
                className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#FF3B30] focus:border-transparent h-12"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-white/50 mb-2">
                Phone
              </label>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                data-testid="input-phone"
                placeholder="+1 (555) 000-0000"
                className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#FF3B30] focus:border-transparent h-12"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-white/50 mb-2">
                Budget Range
              </label>
              <select
                name="budget_range"
                value={formData.budget_range}
                onChange={handleChange}
                data-testid="select-budget"
                className="w-full h-12 px-3 bg-[#1A1A1A] border border-white/10 rounded-md text-white text-sm focus:ring-2 focus:ring-[#FF3B30] focus:border-transparent appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#1A1A1A]">Select budget range</option>
                {budgetRanges.map((r) => (
                  <option key={r} value={r} className="bg-[#1A1A1A]">
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-white/50 mb-2">
                Collaboration Type
              </label>
              <select
                name="collaboration_type"
                value={formData.collaboration_type}
                onChange={handleChange}
                data-testid="select-collab-type"
                className="w-full h-12 px-3 bg-[#1A1A1A] border border-white/10 rounded-md text-white text-sm focus:ring-2 focus:ring-[#FF3B30] focus:border-transparent appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#1A1A1A]">Select type</option>
                {collabTypes.map((t) => (
                  <option key={t} value={t} className="bg-[#1A1A1A]">
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-xs uppercase tracking-[0.15em] font-bold text-white/50 mb-2">
              Message *
            </label>
            <Textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              data-testid="input-message"
              placeholder="Tell us about your campaign goals and how you'd like to collaborate..."
              rows={5}
              className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#FF3B30] focus:border-transparent resize-none"
            />
          </div>

          {status === "error" && (
            <div
              data-testid="form-error"
              className="mb-6 p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-lg text-sm text-[#FF3B30]"
            >
              {errorMsg}
            </div>
          )}

          {status === "success" ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="form-success"
              className="flex items-center justify-center gap-3 py-4 bg-green-500/10 border border-green-500/20 rounded-lg"
            >
              <CheckCircle size={24} weight="fill" className="text-green-400" />
              <span className="text-green-400 font-bold text-sm uppercase tracking-wider">
                Inquiry Sent Successfully!
              </span>
            </motion.div>
          ) : (
            <button
              type="submit"
              disabled={status === "loading"}
              data-testid="submit-inquiry-button"
              className="w-full bg-[#FF3B30] text-white py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#FF5C54] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {status === "loading" ? (
                <>
                  <CircleNotch size={20} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={20} weight="bold" />
                  Send Inquiry
                </>
              )}
            </button>
          )}
        </motion.form>
      </div>
    </section>
  );
}
